import { AdminErrorState } from "@/components/admin/AdminStates";
import { reportAdminError } from "@/lib/reportAdminError";
import {
  canAutoRetryChunkRecovery,
  clearChunkRecoveryAttempt,
  hardReloadAdminShell,
  hasCacheBustParam,
  isChunkLoadError,
  isClerkChunkError,
  markChunkRecoveryAttempt,
  reloadWithCacheBust,
} from "@/lib/chunkLoadRecovery";
import { preloadClerkForAdmin } from "@/lib/preloadClerkForAdmin";
import { Component, type ErrorInfo, type ReactNode } from "react";

type Props = { children: ReactNode; /** Reset boundary when route changes */ resetKey?: string };
type State = { error: Error | null; autoRetrying: boolean };

function errorCopy(error: Error): { title: string; description: string } {
  if (isClerkChunkError(error)) {
    return {
      title: "No pudimos cargar la sesión",
      description:
        "El módulo de inicio de sesión no terminó de descargarse (red lenta, VPN o caché viejo). Recargá la página o reintentá en unos segundos.",
    };
  }
  if (isChunkLoadError(error)) {
    if (hasCacheBustParam()) {
      return {
        title: "No se pudo cargar esta pantalla",
        description:
          "Ya recargamos una vez tras el deploy. Tocá «Recarga limpia» (fuerza archivos nuevos) o abrí la página en una pestaña privada. La sesión de Clerk no se pierde.",
      };
    }
    return {
      title: "Actualizamos el panel",
      description:
        "Hay una versión nueva del admin o un archivo temporal no disponible. Recargá para continuar — no perdés la sesión.",
    };
  }
  // Real render/runtime errors — don't mask as deploy/chunk issues.
  const detail = (error.message || "").trim().slice(0, 280);
  return {
    title: "Error en esta página del admin",
    description: detail
      ? `${detail} — reintentá o recargá. Si persiste, avisá con esta frase.`
      : "Error inesperado al renderizar. Reintentá o recargá la página.",
  };
}

export class AdminChunkErrorBoundary extends Component<Props, State> {
  state: State = { error: null, autoRetrying: false };

  static getDerivedStateFromError(error: Error): State {
    return { error, autoRetrying: false };
  }

  componentDidUpdate(prevProps: Props) {
    // Route change → clear sticky error so the next page can load.
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null, autoRetrying: false });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const page =
      typeof window !== "undefined"
        ? window.location.pathname
        : "/admin";
    reportAdminError({
      page,
      message: error.message,
      fingerprint: isChunkLoadError(error)
        ? `chunk:${error.name}`
        : `render:${error.name}`,
      action: "chunk_boundary",
    });
    void info;

    if (isChunkLoadError(error) && canAutoRetryChunkRecovery() && !this.state.autoRetrying) {
      this.setState({ autoRetrying: true });
      markChunkRecoveryAttempt();
      preloadClerkForAdmin();
      window.setTimeout(() => {
        reloadWithCacheBust();
      }, 1200);
    }
  }

  private handleRetry = () => {
    clearChunkRecoveryAttempt();
    preloadClerkForAdmin();
    this.setState({ error: null, autoRetrying: false });
  };

  private handleReload = () => {
    void hardReloadAdminShell();
  };

  render() {
    const { error, autoRetrying } = this.state;
    if (!error) return this.props.children;

    const copy = errorCopy(error);
    const isChunk = isChunkLoadError(error);

    if (autoRetrying) {
      return (
        <AdminErrorState
          title="Recuperando panel…"
          description="Descargando los archivos nuevos del admin. Esto tarda unos segundos."
          className="min-h-[50vh]"
        />
      );
    }

    return (
      <AdminErrorState
        title={copy.title}
        description={copy.description}
        retry={this.handleRetry}
        retryLabel={isChunk ? "Reintentar sin recargar" : "Reintentar"}
        action={
          <button
            type="button"
            onClick={this.handleReload}
            className="rounded-md border border-white/20 bg-white/10 px-3 py-1.5 text-[12px] font-medium text-white transition hover:bg-white/15"
          >
            Recarga limpia
          </button>
        }
        className="min-h-[50vh]"
      />
    );
  }
}
